import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useGetSalesHistoryQuery } from 'services/api/sales';
import { setPage, selectTabFilters } from 'services/salesHistorySlice/salesHistorySlice';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import AllPage from '../components/AllPage';

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
`;

const MobileListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #666;
  font-size: 16px;
`;

const AllTab = ({ active, skip }: { active: boolean; skip: boolean }) => {
  const { isMobile } = useDeviceInfo();
  const dispatch = useDispatch();
  const { pages, perPage } = useSelector((state: any) => state.salesHistory);
  const filters = useSelector((state: any) => selectTabFilters(state, 'all'));
  const pageCount = pages['all'] || 1;

  // Query the last page to detect loading state & determine if more pages exist
  const { data: lastPageData, isFetching } = useGetSalesHistoryQuery(
    {
      limit: perPage,
      offset: pageCount,
      ...filters
    },
    { skip }
  );

  // Use page 1 data for total count (most reliably cached)
  const { data: firstPageData } = useGetSalesHistoryQuery(
    {
      limit: perPage,
      offset: 1,
      ...filters
    },
    { skip }
  );

  const totalCount = Number(firstPageData?.count ?? lastPageData?.count ?? 0);
  const canLoadMore = perPage * pageCount < totalCount;
  const isEmpty = !isFetching && totalCount === 0;

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && active && canLoadMore && !isFetching) {
          dispatch(setPage(pageCount + 1));
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canLoadMore, isFetching, pageCount, active, dispatch]);

  if (isEmpty) {
    return (
      <EmptyState>
        Once you list your first vehicle, you will see the sale status of that vehicle in this section.
      </EmptyState>
    );
  }

  const pageNumbers = Array.from({ length: pageCount }, (_, i) => i + 1);

  const Wrapper = isMobile ? MobileListWrapper : GridWrapper;

  return (
    <>
      <Wrapper>
        {pageNumbers.map(page => (
          <AllPage key={page} page={page} />
        ))}
      </Wrapper>

      <LoaderWrapper>
        <>
          {isFetching && <CircularProgress size={32} />}
          <Sentinel ref={sentinelRef} />
        </>
      </LoaderWrapper>
    </>
  );
};

export default AllTab;
